import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, DependencyDto, IValueViewModel } from '../../models';
import { ReadResult } from '../identifierAnalysis/ReadResult';
import { IDateRangeService, ILoggerService } from '../../services/general';
import { IidentifierAnalysisService } from '../identifierAnalysis/identifierAnalysis.service';
import { refreshClick } from '../../common';

class IdentifierAnalysisPanesController implements angular.IComponentController {
    dependencyDto: DependencyDto[];
    guid: string;
    isLoading: boolean;
    isLoadingMore: boolean;
    loadingMessage: string;
    maxLevel: number;
    measuredDto: DependencyDto[];
    notShownCount: number;
    observedIdentifier: DependencyDto[];
    readData: ReadResult;
    selectedLevel: number;
    viewModels: { [guid: string]: IValueViewModel };
    private _allDependencies: DependencyDto[];

    static $inject = [
        '$scope',
        '$q',
        'dateRangeService',
        'identifierAnalysisService',
        'loggerService'
    ];

    constructor(private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _dateRangeService: IDateRangeService,
        private _identifierAnalysisService: IidentifierAnalysisService,
        private _loggerService: ILoggerService) {
    }

    $onInit() {
        this._$scope.$on(refreshClick, (_event, data) => {
            this.refresh(data.force);
        });
    }

    $onChanges(_changesObj: angular.IOnChangesObject) {
        if (this.guid && this.viewModels) {
            this.updateSeries(this.guid);
        }
    }

    changeLevel(level: number, includeRoot: boolean = false) {
        const readData = this.readData || {
            xMin: null,
            xMax: null,
            data: [],
            errors: {}
        };
        this.selectedLevel = level;
        const currentDependencies = this.dependencyDto;
        this.dependencyDto = this._allDependencies.filter(d => d.depth <= level && !d.isMeasured);
        this.notShownCount = this._allDependencies.length
            - this.dependencyDto.length;
        let toLoad = this.dependencyDto.filter(d => currentDependencies.indexOf(d) < 0);

        if (includeRoot) {
            toLoad = toLoad
                .concat(this.observedIdentifier)
                .concat(this.measuredDto);
        }
        if (toLoad.length) {
            return this.readIdentifierData(includeRoot, toLoad, readData);
        }

        const toRemove = currentDependencies.filter(d => this.dependencyDto.indexOf(d) < 0);
        if (toRemove.length) {
            const guids = toRemove.map(d => d.guid);
            const data = readData.data
                .filter(d => guids.indexOf(d.guid) < 0)
                .sort((a, b) => a.timeStamp < b.timeStamp ? -1 : 1);
            const errors = {};
            for (const key of Object.keys(readData.errors)) {
                if (guids.indexOf(key) < 0) {
                    errors[key] = readData.errors[key];
                }
            }
            this.readData = (Object as any).assign({}, readData, { data, errors });
        }
    }

    private readIdentifierData(includeRoot: boolean, toLoad: DependencyDto[], readData: ReadResult) {
        this.isLoading = !!includeRoot;
        this.isLoadingMore = !includeRoot;
        this.loadingMessage = 'Loading data';
        return this._identifierAnalysisService
            .readData(toLoad)
            .then(newData => {
                const data = readData.data
                    .concat(newData.data)
                    .sort((a, b) => a.timeStamp < b.timeStamp ? -1 : 1);
                const errors = (Object as any).assign({}, readData.errors, newData.errors);
                this.readData = (Object as any).assign({}, newData, { data, errors });
            })
            .finally(() => {
                this.isLoading = false;
                this.isLoadingMore = false;
            });
    }

    private initReadData() {
        if (this.readData) {
            this.readData = {
                xMin: this.readData.xMin,
                xMax: this.readData.xMax,
                errors: {},
                data: []
            };
        }
    }

    private updateSeries(guid: string) {
        this.isLoading = true;
        this.loadingMessage = 'Loading dependencies';
        const vm = this.viewModels[guid];
        let message: string = null;
        if (!vm) {
            message = `No view model with guid ${guid}`;

        } else if (!vm.groupList || !vm.groupList.length) {
            message = `View model does not have group`;
        }
        if (message) {
            this._loggerService.logError(message, false);
            return this._$q.reject(message);
        }
        else {
            const refGroupGuid = vm.groupList[0].guid;
            this.initReadData();
            this._allDependencies = [];
            this.dependencyDto = [];
            this.observedIdentifier = [];
            this.measuredDto = [];
            return this._identifierAnalysisService.readSeries(guid, this.viewModels, refGroupGuid)
                .then(data => {
                    this._allDependencies = data.dependencyDto;
                    this.observedIdentifier = data.observedIdentifier;
                    this.measuredDto = data.measuredDto;
                    this.maxLevel = data.dependencyDto.length
                        ? _.max(data.dependencyDto.map(d => d.depth))
                        : 1;

                    const top20 = _.take(data.dependencyDto, 20);
                    const top20level = _.max(top20.map(d => d.depth));
                    return this.changeLevel(top20level, true);
                })
                .finally(() => this.isLoading = false);
        }
    }

    private refresh(force = false) {
        if (this._dateRangeService.initialized() && this.guid && this.dependencyDto) {
            this.loadingMessage = 'Loading data';
            this.isLoading = true;
            return this._identifierAnalysisService
                .readData(this.dependencyDto.concat(this.observedIdentifier).concat(this.measuredDto), force)
                .then(data => this.readData = data)
                .finally(() => this.isLoading = false);
        }
    }
}

export const wtIdentifierAnalysisPanes: ComponentDefinition = {
    componentId: 'wtIdentifierAnalysisPanes',
    controller: IdentifierAnalysisPanesController,
    template: require('./identifierAnalysisPanes.component.html'),
    controllerAs: 'vm',
    bindings: {
        guid: '<',
        isLoading: '=?',
        viewModels: '<',
    }
};
